import axios from 'axios'
import isMobileWebviewSession from './mobileWebview'

var RETRY_STATUSES = [500, 502, 503, 504]
var DEFAULT_MAX_RETRIES = 3
var DEFAULT_BASE_DELAY = 1000
var UPLOAD_TIMEOUT_MS = 60000
var POLL_TIMEOUT_MS = 15000

function isPlatformSentinelError (error) {
  if (!error.response) {
    return true
  }
  if (error.response.status >= 500) {
    return true
  }
  return false
}

function isRetryable (error) {
  if (error.response && error.response.status === 401) {
    return false
  }
  if (error.response && RETRY_STATUSES.indexOf(error.response.status) !== -1) {
    return true
  }
  if (!error.response) {
    return true
  }
  return false
}

function attachAuthInterceptor (instance) {
  instance.interceptors.request.use(function (request) {
    if (global.app && global.app.$user && global.app.$user.sessionSettings && global.app.$user.sessionSettings.Token) {
      request.headers['Authorization'] = 'Bearer ' + global.app.$user.sessionSettings.Token
    }
    return request
  }, null, { synchronous: true })
}

function attachResponseInterceptor (instance) {
  instance.interceptors.response.use(
    function onSuccess (response) {
      return response.data
    },
    function onError (error) {
      if (error.response && error.response.status === 401) {
        // Only treat a 401 as session-expiry when we actually sent a bearer. A 401 on a
        // request that carried no Authorization header means we fired before the app was
        // ready — e.g. an httpPlatform call in a component created() during a hard-refresh
        // bootstrap, before App.vue's `global.app = new Vue(...)` is assigned, so the request
        // interceptor above couldn't attach the token. That is not a real expiry; logging out
        // would kick a perfectly-authenticated user. Reject and let the caller handle it.
        var sentAuth = error.config && error.config.headers && error.config.headers['Authorization']
        if (sentAuth && !isMobileWebviewSession() && global.app && global.app.$user && typeof global.app.$user.logout === 'function') {
          global.app.$user.logout()
        }
        return Promise.reject(error)
      }

      if (isPlatformSentinelError(error)) {
        error.isPlatformSentinelError = true
      }

      return Promise.reject(error)
    }
  )
}

function attachRetryInterceptor (instance, maxRetries, baseDelay) {
  instance.interceptors.response.use(null, function retryOnError (error) {
    var config = error.config
    if (!config) {
      return Promise.reject(error)
    }

    config.__retryCount = config.__retryCount || 0

    if (!isRetryable(error) || config.__retryCount >= maxRetries) {
      return Promise.reject(error)
    }

    config.__retryCount++
    var delayMs = baseDelay * Math.pow(2, config.__retryCount - 1)

    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(instance(config))
      }, delayMs)
    })
  })
}

function setupInstance (instance, maxRetries, baseDelay) {
  attachAuthInterceptor(instance)
  attachResponseInterceptor(instance)
  attachRetryInterceptor(instance, maxRetries, baseDelay)
}

var httpPlatform = axios.create({
  baseURL: process.env.PLATFORM_URL,
  withCredentials: true,
  timeout: UPLOAD_TIMEOUT_MS
})

setupInstance(httpPlatform, DEFAULT_MAX_RETRIES, DEFAULT_BASE_DELAY)

httpPlatform.create = function createPlatformInstance (options) {
  options = options || {}
  var maxRetries = typeof options.maxRetries === 'number' ? options.maxRetries : DEFAULT_MAX_RETRIES
  var baseDelay = typeof options.baseDelay === 'number' ? options.baseDelay : DEFAULT_BASE_DELAY
  var axiosConfig = {}

  Object.keys(options).forEach(function (key) {
    if (key !== 'maxRetries' && key !== 'baseDelay') {
      axiosConfig[key] = options[key]
    }
  })

  if (!axiosConfig.baseURL) {
    axiosConfig.baseURL = process.env.PLATFORM_URL
  }
  if (axiosConfig.withCredentials === undefined) {
    axiosConfig.withCredentials = true
  }
  if (axiosConfig.timeout === undefined) {
    axiosConfig.timeout = UPLOAD_TIMEOUT_MS
  }

  var instance = axios.create(axiosConfig)
  setupInstance(instance, maxRetries, baseDelay)

  return instance
}

httpPlatform.isPlatformSentinelError = isPlatformSentinelError
httpPlatform.UPLOAD_TIMEOUT_MS = UPLOAD_TIMEOUT_MS
httpPlatform.POLL_TIMEOUT_MS = POLL_TIMEOUT_MS

export default httpPlatform



// WEBPACK FOOTER //
// ./src/core/api/httpPlatform.js